import { useState, useEffect } from 'react'

interface GeoPrice {
  current: string
  compareAt: string
  savings: string
  currency: string
  currencyCode: string
  loading: boolean
  convertPrice: (usdAmount: number) => string
  checkoutUrl: string
}

interface PriceConfig {
  current: string
  compareAt: string
  savings: string
  currency: string
  currencyCode: string
  rate: number
  decimals: boolean
  separator: '.' | ','
  checkoutUrl: string
}

const CHECKOUT_AR =
  'https://de6b-claudio.systeme.io/100ads?sa=sa0117913503fbde2eb61448822a19f2bcbc92048f'
const CHECKOUT_DEFAULT =
  'https://de6b-claudio.systeme.io/100ads?sa=sa0117913503fbde2eb61448822a19f2bcbc92048f'

const PRICE_MAP: Record<string, PriceConfig> = {
  CL: {
    current: '48.999',
    compareAt: '195.999',
    savings: '146.999',
    currency: 'CLP',
    currencyCode: 'CLP',
    rate: 859.63,
    decimals: false,
    separator: '.',
    checkoutUrl: CHECKOUT_DEFAULT,
  },
  AR: {
    current: '77.700',
    compareAt: '310.800',
    savings: '233.100',
    currency: 'ARS',
    currencyCode: 'ARS',
    rate: 1363.16,
    decimals: false,
    separator: '.',
    checkoutUrl: CHECKOUT_AR,
  },
  CO: {
    current: '210.999',
    compareAt: '843.999',
    savings: '632.999',
    currency: 'COP',
    currencyCode: 'COP',
    rate: 3701.74,
    decimals: false,
    separator: '.',
    checkoutUrl: CHECKOUT_DEFAULT,
  },
  MX: {
    current: '979',
    compareAt: '3.916',
    savings: '2.937',
    currency: 'MXN',
    currencyCode: 'MXN',
    rate: 17.18,
    decimals: false,
    separator: '.',
    checkoutUrl: CHECKOUT_DEFAULT,
  },
  UY: {
    current: '2.189',
    compareAt: '8.756',
    savings: '6.567',
    currency: 'UYU',
    currencyCode: 'UYU',
    rate: 38.4,
    decimals: false,
    separator: '.',
    checkoutUrl: CHECKOUT_DEFAULT,
  },
  ES: {
    current: '48,99',
    compareAt: '195,99',
    savings: '147,00',
    currency: 'EUR',
    currencyCode: 'EUR',
    rate: 0.8595,
    decimals: true,
    separator: ',',
    checkoutUrl: CHECKOUT_DEFAULT,
  },
  US: {
    current: '57',
    compareAt: '228',
    savings: '171',
    currency: 'USD',
    currencyCode: 'USD',
    rate: 1,
    decimals: false,
    separator: '.',
    checkoutUrl: CHECKOUT_DEFAULT,
  },
}

const DEFAULT_KEY = 'US'

function formatLocalPrice(amount: number, config: PriceConfig): string {
  if (config.decimals) {
    // EUR-style: comma as decimal separator
    const rounded = Math.round(amount * 100) / 100
    const parts = rounded.toFixed(2).split('.')
    return `${parts[0]}${config.separator}${parts[1]}`
  } else {
    // LATAM-style: dot as thousands separator, no decimals
    const rounded = Math.round(amount)
    return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }
}

function createConverter(config: PriceConfig) {
  return (usdAmount: number): string => {
    const localAmount = usdAmount * config.rate
    return formatLocalPrice(localAmount, config)
  }
}

const DEFAULT_PRICE = PRICE_MAP[DEFAULT_KEY]

export function useGeoPrice(): GeoPrice {
  const [state, setState] = useState<{ config: PriceConfig; loading: boolean }>(
    {
      config: DEFAULT_PRICE,
      loading: true,
    },
  )

  useEffect(() => {
    let cancelled = false

    async function detectCountry() {
      try {
        const res = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(4000),
        })
        if (!res.ok) throw new Error('API error')
        const data = await res.json()
        const countryCode = data.country_code || data.country

        if (!cancelled && countryCode) {
          const matched = PRICE_MAP[countryCode] || DEFAULT_PRICE
          setState({ config: matched, loading: false })
        } else if (!cancelled) {
          setState({ config: DEFAULT_PRICE, loading: false })
        }
      } catch {
        try {
          const res = await fetch(
            'https://ip-api.com/json/?fields=countryCode',
            {
              signal: AbortSignal.timeout(3000),
            },
          )
          if (!res.ok) throw new Error('Fallback API error')
          const data = await res.json()
          const countryCode = data.countryCode

          if (!cancelled && countryCode) {
            const matched = PRICE_MAP[countryCode] || DEFAULT_PRICE
            setState({ config: matched, loading: false })
          } else if (!cancelled) {
            setState({ config: DEFAULT_PRICE, loading: false })
          }
        } catch {
          if (!cancelled) {
            setState({ config: DEFAULT_PRICE, loading: false })
          }
        }
      }
    }

    detectCountry()
    return () => {
      cancelled = true
    }
  }, [])

  return {
    current: state.config.current,
    compareAt: state.config.compareAt,
    savings: state.config.savings,
    currency: state.config.currency,
    currencyCode: state.config.currencyCode,
    loading: state.loading,
    convertPrice: createConverter(state.config),
    checkoutUrl: state.config.checkoutUrl,
  }
}
